import { DOCUMENTS_TAB, TabPermission } from "./candidate-things";
import { isNil } from "lodash";

/** The little of a `SharedTab`/`CandidateProfileConfigTab` this module needs, structurally typed so the backend class, the frontend's generated fragment and a migration's raw document all satisfy it without importing each other. */
export interface DocumentTabLike {
  key: string;
  /** For the DOCUMENTS tab this is an `AllowedUploadFileTypes` VALUE (`"PASSPORT"`), not a Candidate path: the one exception to `TabField.name`'s meaning. A document type has no control or place in the Candidate document, so it uses the same entry shape purely to be maskable like any other tab. */
  sharedFields?: { name: string; isEnabled?: boolean | null }[] | null;
  fullTabPermission?: TabPermission | null;
}

/** The flat document-type model every consumer reads (`EffectiveSharing.sharedFileTypes`/`.shareAllFileTypes`), produced from or turned into the documents tab's field entries. */
export interface SharedDocumentTypes {
  sharedFileTypes: string[];
  shareAllFileTypes: boolean;
}

/**
 * Which document types a set of shared tabs grants, from the DOCUMENTS tab's per-type entries; the single place that translation happens
 * (see {@link DOCUMENTS_TAB}). Documents follow their tab like every other field:
 * - tab absent -> nothing.
 * - whole-tab permission (covers every field) -> ALL types; what a "simple"-mode sharing stores.
 * - a list -> exactly those types.
 * - neither -> nothing (`isSharedTab`).
 *
 * `shareAllFileTypes` stays next to the list because consumers read it as "and anything added later", which a snapshot cannot express.
 */
export function toSharedDocumentTypes(
  tabs: DocumentTabLike[] | null | undefined,
  sharingDocumentTypes?: Partial<SharedDocumentTypes> | null
): SharedDocumentTypes {
  const documents = (tabs ?? []).find((tab) => tab.key === DOCUMENTS_TAB);
  if (!documents) {
    return { sharedFileTypes: [], shareAllFileTypes: false };
  }

  const types = documents.sharedFields?.map((field) => field.name) ?? [];
  if (isNil(documents.fullTabPermission) && types.length === 0) {
    return { sharedFileTypes: [], shareAllFileTypes: false };
  }
  if (!isNil(documents.fullTabPermission)) {
    // A whole-tab permission without per-type detail: the sharing's own document fields still decide for a row the migrations (v302, v306) have not moved into the tab or mask; otherwise this means "every type".
    if (sharingDocumentTypes?.sharedFileTypes?.length && !sharingDocumentTypes.shareAllFileTypes) {
      return { sharedFileTypes: sharingDocumentTypes.sharedFileTypes, shareAllFileTypes: false };
    }
    return { sharedFileTypes: [], shareAllFileTypes: true };
  }
  return { sharedFileTypes: types, shareAllFileTypes: false };
}

/**
 * Whether the shared tabs let a document of `type` be WRITTEN (uploaded, replaced, edited, deleted): the per-type "editable" of a mask or
 * detailed sharing. Only decides among the types {@link toSharedDocumentTypes} shares; whether the sharing may write at all (its sharing
 * type) is the caller's business.
 * - documents tab absent -> no type.
 * - whole-tab permission -> every type, unless visible only (`Selected`).
 * - neither a permission nor a list -> no type (`isSharedTab`).
 * - list -> the listed types not marked read-only. An entry without `isEnabled` counts as writable: sharings from the test phase store a
 *   plain selection that way, always writable for a collaboration. Simple mode and the rollout write `isEnabled: true` explicitly.
 */
export function isDocumentTypeWritable(tabs: DocumentTabLike[] | null | undefined, type: string): boolean {
  const documents = (tabs ?? []).find((tab) => tab.key === DOCUMENTS_TAB);
  if (!documents) {
    return false;
  }
  if (!isNil(documents.fullTabPermission)) {
    return documents.fullTabPermission !== TabPermission.Selected;
  }

  const fields = documents.sharedFields ?? [];
  return fields.some((field) => field.name === type && field.isEnabled !== false);
}

/** The inverse of {@link toSharedDocumentTypes}: the documents tab's `sharedFields` for a flat selection. `null` means no per-type detail, so the caller writes the whole tab at its level (every type); a tab with neither level nor detail shares nothing. Used by the sharing editor and the migration moving `sharedFileTypes`/`shareAllFileTypes` into the tab. */
export function toDocumentTabFields(
  types: Partial<SharedDocumentTypes> | null | undefined
): { name: string; isEnabled: boolean }[] | null {
  if (types?.shareAllFileTypes) {
    return null;
  }
  const selected = types?.sharedFileTypes ?? [];
  // Explicitly writable: the field editor reads a missing flag as "visible only"; the sharing type still caps it.
  return selected.length > 0 ? selected.map((name) => ({ name, isEnabled: true })) : null;
}
